import { useEffect, useMemo, useRef, useState } from "react";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import { useToggle } from "../../hooks/useToggle";
import PageMeta from "../../components/common/PageMeta";
import { useSimplePagination } from "../../hooks/useSimplePagination";
import CommonTable, { type ColumnConfig } from "../../components/common/Table";
import { ALLOW_REFUND_USERS_COUNT, PAGINATION_PER_PAGE } from "../../utils/constants";
import debounce from "lodash.debounce";
import Button from "../../components/ui/button/Button";
import { DollarLineIcon } from "../../icons";
import {
  getLastPaymentDetailsByUserId,
  getUserProcessedRefund,
  initiateRefundPayStack,
  referralsList,
} from "../../services/referral";
import type { IReferral } from "../../types/referral";
import { useParams } from "react-router";
import { toast } from "react-toastify";
import type { IUserRefund } from "../../types/user";
import Confirm, { type ConfirmHandle } from "../../components/confirm";
import type { TUserPlan } from "../../types/payment";

const columns: ColumnConfig<IReferral>[] = [
  {
    key: "id",
    header: "ID",
    render(_, index) {
      return index + 1;
    },
  },
  {
    key: "referralCode",
    header: "Referral Code",
  },
  {
    key: "referredByUser",
    header: "Referred By Name",
    className: "max-w-64 truncate",
    render(item) {
      return item.referredByUser?.name || "N/A";
    },
  },
  {
    key: "updatedAt",
    header: "Referred By Email",
    className: "max-w-64 truncate",
    render(item) {
      return item.referredByUser?.email || "N/A";
    },
  },
  {
    key: "user",
    header: "Referred User Name",
    render(item) {
      return item.user?.name || "N/A";
    },
  },
  {
    key: "createdAt",
    header: "Referred User Email",
    render(item) {
      return item.user?.email || "N/A";
    },
  },
  {
    key: "isUsed",
    header: "Is Paid",
    render(item) {
      return item.isUsed ? "Yes" : "No";
    },
  },
];

const ReferralsPage = () => {
  const confirmRef = useRef<ConfirmHandle>(null);
  const [referrals, setReferrals] = useState<IReferral[]>([]);
  const { userId } = useParams() as { userId: string };
  const [showRefund, setShowRefund] = useState(false);
  const [refund, setRefund] = useState<IUserRefund | boolean | null>(null);
  const [userPlan, setUserPlan] = useState<TUserPlan | null>(null);

  const { state: isLoading, on: showLoader, off: hideLoader } = useToggle();

  const {
    state: isRefundInProgress,
    on: startRefundProcess,
    off: completeRefundProcess,
  } = useToggle();

  const { currentPage, totalPages, onPageChange, setTotalPages } =
    useSimplePagination();

  const fetchEvents = async () => {
    if (isLoading) return;
    showLoader();
    const res = await referralsList(userId, {
      params: {
        page: currentPage,
        limit: PAGINATION_PER_PAGE,
      },
    }).finally(() => {
      hideLoader();
    });
    setReferrals(res.rows);
    setTotalPages(res.count);
    setShowRefund(
      res.rows.filter((item: IReferral) => item.isUsed).length >= ALLOW_REFUND_USERS_COUNT
    );
  };

  const getUserRefund = () => {
    getUserProcessedRefund(userId).then((res) => {
      setRefund(res);
    });
  };

  const getLastUserPayment = () => {
    getLastPaymentDetailsByUserId(userId).then((res) => {
      setUserPlan(res);
    });
  };

  useEffect(() => {
    getUserRefund();
    getLastUserPayment();
    // eslint-disable-next-line
  }, [userId]);

  const debouncedResults = useMemo(() => {
    return debounce(fetchEvents, 500);
    // eslint-disable-next-line
  }, [currentPage]);

  useEffect(() => {
    debouncedResults();
    return () => {
      debouncedResults.cancel();
    };
  }, [debouncedResults]);

  const initiateRefund = async () => {
    if (isRefundInProgress) return;
    startRefundProcess();
    try {
      await initiateRefundPayStack(userId);
      toast.success("Refund initiated successfully");
      setRefund(true);
    } catch (e) {
      console.error(e);
      toast.error((e as { message?: string })?.message ?? "Refund failed");
    } finally {
      completeRefundProcess();
    }
  };

  return (
    <div>
      <PageMeta title="Users" description="List of users" />
      <PageBreadcrumb pageTitle="Users" />
      <div className="flex justify-end items-center my-5 gap-5">
        {refund instanceof Object ? (
          <p className="text-warning-500 text-sm">
            Your refund of {userPlan?.amount} ZAR has already been processed on{" "}
            {new Date((refund as IUserRefund).createdAt).toLocaleString()}
          </p>
        ) : null}
        {showRefund || refund ? (
          <Button
            size="sm"
            startIcon={<DollarLineIcon />}
            disabled={isRefundInProgress || !!refund || !userPlan}
            onClick={confirmRef.current?.open}
          >
            Refund
          </Button>
        ) : null}
      </div>
      <CommonTable
        columns={columns}
        data={referrals}
        isLoading={isLoading}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={onPageChange}
        showPagination
      />
      <Confirm
        title={`Confirm Refund of ${userPlan?.amount} ZAR?`}
        subtitle="The user will be notified via email once refund is processed"
        onConfirm={initiateRefund}
        ref={confirmRef}
        confirmDisabled={isRefundInProgress || Boolean(refund)}
      />
    </div>
  );
};

export default ReferralsPage;
